import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import AppealHistoryPanel from "../../components/modals/appealSummaryModal/appealHistoryPanel";
import AppealSummaryModal from "../../components/modals/appealSummaryModal/appealSummaryModal";
import { useTablesContext } from "../../context/tablesContextProvider";
import { PharmaStpFileModel } from "../../data-model/nycbsPharmaOrder";
import { ModalId } from "../../enums/modalId";
import { SecondStpStatus } from "../../enums/secondStpStatus";
import { TableNames } from "../../enums/tableNames";
import { useModalOpener } from "../../hooks/useModalOpener";
import { closeModal } from "../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../redux/store/store";

/**
 * Layout wrapper for pharma oncology appeals routes.
 * Renders the outlet alongside the Appeal Summary modal opened from the
 * Appeal Info View/Edit column (Case Appeals copy of the Non-EPA modal).
 */
const PharmaOncologyAppealsLayout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { setTableDataForContext } = useTablesContext();

  useModalOpener();

  /**
   * Clears the opened modal in redux.
   */
  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  /**
   * Refetches the Case Appeals table after a successful Edit Summary save.
   */
  const handleSummarySuccess = () => {
    setTableDataForContext(TableNames.ONCOLOGY_CASE_APPEALS_ORDERS, {
      shouldRefetch: true,
    });
  };

  const isAppealSummaryModalOpen =
    openedModalId === ModalId.APPEAL_SUMMARY_MODAL;
  const isAppealHistoryOpen = openedModalId === ModalId.APPEAL_HISTORY_PANEL;
  const appealSummaryModalData =
    metaData && "data" in metaData
      ? (metaData as { data: PharmaStpFileModel }).data
      : null;

  return (
    <div className="pharma-submission__layout flex h-full w-full overflow-hidden">
      <div className="pharma-submission__outlet w-full flex-1">
        <Outlet />
      </div>
      {isAppealSummaryModalOpen && appealSummaryModalData && (
        <AppealSummaryModal
          key={appealSummaryModalData.identifier}
          data={appealSummaryModalData}
          onClose={handleCloseModal}
          onSuccess={handleSummarySuccess}
          secondStpOutcomeLabel={SecondStpStatus.APPEAL}
          // Every appeal list row is an existing case, so the dialog always
          // edits rather than adds — independent of whether it has a CMM ID.
          title="Edit Summary"
        />
      )}
      {isAppealHistoryOpen && appealSummaryModalData && (
        <AppealHistoryPanel
          key={appealSummaryModalData.identifier}
          isOpen={isAppealHistoryOpen}
          data={appealSummaryModalData}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default PharmaOncologyAppealsLayout;
